// src/components/Match.js
import React, { useState, useEffect, useRef } from 'react';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';

const Match = ({ user, rival, myStats, rivalStats, isPaid, onBack }) => {
    const [logs, setLogs] = useState([]);
    const [matchStatus, setMatchStatus] = useState('playing');
    const [score, setScore] = useState({ me: 0, rival: 0 });
    // ★ サーバーが返したステータス（届くまでは props を仮表示）
    const [serverStats, setServerStats] = useState(null);
    const startedRef = useRef(false);

    const shownMyStats = serverStats?.myStats || myStats || {};
    const shownRivalStats = serverStats?.rivalStats || rivalStats || {};

    useEffect(() => {
        // ★ 二重実行を防ぐ。StrictMode や再描画で二度呼ばれると
        //   参加費が二重に引かれてしまう。
        if (startedRef.current) return;
        startedRef.current = true;

        let isMounted = true;

        const run = async () => {
            const addLog = (text, delay) => new Promise(resolve => {
                setTimeout(() => {
                    if (isMounted) setLogs(prev => [...prev, text]);
                    resolve();
                }, delay);
            });

            let data;
            try {
                // ★ 試合の値勝判定・報酬・無料枠はすべてサーバーが決める。
                //   以前はここで勝敗を計算して報酬を書き込んでいたため、
                //   「勝ちました、100コインください」が通ってしまっていた。
                const fns = getFunctions(getApp(), 'asia-northeast1');
                const res = await httpsCallable(fns, 'playArenaMatch')({ rivalUserId: rival.userId });
                data = res?.data;
            } catch (e) {
                console.error('アリーナエラー:', e?.code || '', e?.message || e);
                if (isMounted) {
                    setMatchStatus('finished');
                    Swal.fire('対戦できませんでした', e?.message || 'もう一度お試しください。', 'error');
                }
                return;
            }

            if (!isMounted || !data) return;
            setServerStats({ myStats: data.myStats, rivalStats: data.rivalStats });

            // サーバーから受け取った実況ログを順に表示する
            const delays = [1000, 1000, 800, 800, 1000];
            for (let i = 0; i < data.logs.length; i++) {
                await addLog(data.logs[i], delays[i] ?? 1500);
                if (!isMounted) return;
                // 前半終了・試合終了のタイミングでスコアを反映
                if (data.logs[i].includes('後半戦スタート')) {
                    setScore({ me: Math.min(data.myGoal, 1), rival: Math.min(data.rivalGoal, 1) });
                }
            }

            if (!isMounted) return;
            setScore({ me: data.myGoal, rival: data.rivalGoal });
            setMatchStatus('finished');

            let messageText = `獲得: ${data.rewardAmount} RBコイン`;
            if (data.isPaid) {
                messageText = `参加費: -${data.entryFee} RB\n結果: +${data.rewardAmount} RB\n\n最終獲得: ${data.netReward} RBコイン`;
            }
            Swal.fire({
                title: data.resultText,
                text: messageText,
                icon: data.myGoal > data.rivalGoal ? 'success' : 'info',
                confirmButtonColor: '#d32f2f'
            });
        };

        run();
        return () => { isMounted = false; };
    }, []);

    return (
        <div style={{ minHeight: '100vh', background: '#111', color: 'white', display: 'flex', flexDirection: 'column' }}>
            <Toaster position="top-center" />
            
            <div style={{ background: 'linear-gradient(135deg, #1a237e, #b71c1c)', padding: '15px 20px', display: 'flex', alignItems: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', zIndex: 10 }}>
                {matchStatus === 'finished' ? (
                    <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'white', fontSize: '16px', cursor: 'pointer', padding: '0 10px 0 0' }}>← 戻る</button>
                ) : (
                    <div style={{ width: '45px' }}></div> 
                )}
                <h2 style={{ margin: 0, fontSize: '18px', flex: 1, textAlign: 'center', letterSpacing: '2px' }}>🔥 MATCH 🔥</h2>
                <div style={{ width: '45px' }}></div>
            </div>

            <div style={{ background: '#222', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #444', boxShadow: '0 4px 10px rgba(0,0,0,0.8)' }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '10px', color: '#1976d2', fontWeight: 'bold' }}>POWER: {shownMyStats.total ?? '-'}</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.displayName}</div>
                    <div style={{ fontSize: '42px', fontWeight: '900', color: score.me > score.rival ? '#ffeb3b' : 'white', textShadow: '2px 2px 4px black' }}>{score.me}</div>
                </div>
                <div style={{ fontSize: '24px', fontWeight: '900', color: '#d32f2f', padding: '0 15px', fontStyle: 'italic' }}>VS</div>
                <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '10px', color: '#e65100', fontWeight: 'bold' }}>POWER: {shownRivalStats.total ?? '-'}</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rival.displayName}</div>
                    <div style={{ fontSize: '42px', fontWeight: '900', color: score.rival > score.me ? '#ffeb3b' : 'white', textShadow: '2px 2px 4px black' }}>{score.rival}</div>
                </div>
            </div>

            <div style={{ flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', background: 'radial-gradient(circle, #222 0%, #111 100%)' }}>
                {logs.map((log, index) => (
                    <div key={index} style={{ background: 'rgba(255,255,255,0.1)', padding: '15px', borderRadius: '8px', fontSize: '14px', borderLeft: '4px solid #4caf50', animation: 'fadeIn 0.5s ease-in', boxShadow: '0 2px 5px rgba(0,0,0,0.5)' }}>
                        {log}
                    </div>
                ))}
                {matchStatus === 'playing' && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#ffeb3b', fontWeight: 'bold', animation: 'pulse 1s infinite alternate' }}>
                        試合進行中... ⚽️💨
                    </div>
                )}
            </div>
            
            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulse {
                    from { opacity: 0.5; }
                    to { opacity: 1; }
                }
                `}
            </style>
        </div>
    );
};

export default Match;